import { AlertDialog, Badge, Button, Card, Flex, Grid, Link, Separator, Text } from "@radix-ui/themes";
import { Link as InertiaLink, router } from "@inertiajs/react";
import {
  Calendar,
  Camera,
  Car,
  CheckCircle,
  ClipboardList,
  Clock,
  Hourglass,
  Images,
  MapPin,
  Trash2,
  Trophy,
  Users,
  Users2,
  XCircle,
} from "lucide-react";
import { ManagerEventsIndexEvent } from "@/types/manager_events";

interface Props {
  event: ManagerEventsIndexEvent;
  /** Rendered inside a mobile card that already shows this record's identity, so
   *  drop whatever the card is already saying. Each panel keeps what the card can't. */
  compact?: boolean;
}

interface CountCardProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  color?: string;
  /** When given, the whole card is the way through to the applications. */
  href?: string;
}

function CountCard({ icon, label, count, color, href }: CountCardProps) {
  const body = (
    <Card style={href ? { cursor: "pointer" } : undefined}>
      <Flex direction="column" gap="1">
        <Flex align="center" gap="1" style={{ color: color ?? "inherit" }}>
          {icon}
          <Text size="1" color="gray">
            {label}
          </Text>
        </Flex>
        <Text size="7" weight="bold" style={{ color: color ?? "inherit" }}>
          {count}
        </Text>
      </Flex>
    </Card>
  );

  if (!href) return body;

  return (
    <InertiaLink href={href} style={{ textDecoration: "none", color: "inherit" }}>
      {body}
    </InertiaLink>
  );
}

export default function EventDetailPanel({ event, compact = false }: Props) {
  const accepts = [
    event.accepts_media && { label: "Media", icon: <Camera size={13} /> },
    event.accepts_staff && { label: "Staff", icon: <Users size={13} /> },
    event.accepts_drivers && { label: "Drivers", icon: <Car size={13} /> },
    event.accepts_teams && { label: "Teams", icon: <Users2 size={13} /> },
  ].filter(Boolean) as { label: string; icon: React.ReactNode }[];

  return (
    <Flex direction="column" gap="4">
      {/* Header. In a card, the name, track and date are already on the card — but
          the seasons, the applications badge and the link to the public page are
          not, and this is the only place they appear. So keep those. */}
      <Flex direction="column" gap="2">
        {!compact && (
          <Link href={event.show_path} size="5" weight="bold">
            {event.name}
          </Link>
        )}
        {event.season_names.length > 0 && (
          <Flex align="center" gap="1">
            <Trophy size={14} />
            <Text size="2" color="gray">
              {event.season_names.join(", ")}
            </Text>
          </Flex>
        )}
        {!compact && (
          <Flex align="center" gap="1">
            <MapPin size={14} />
            <Text size="2" color="gray">
              {event.track_name}
            </Text>
          </Flex>
        )}
        <Flex align="center" justify="between" gap="2">
          {compact ? (
            <Link href={event.show_path} size="2">
              View public page
            </Link>
          ) : (
            <Flex align="center" gap="1">
              <Calendar size={14} />
              <Text size="2" color="gray">
                {event.date}
              </Text>
            </Flex>
          )}
          <Badge color={event.applications_open ? "green" : "gray"} variant="soft">
            {event.applications_open ? "Applications Open" : "Applications Closed"}
          </Badge>
        </Flex>
      </Flex>

      <Separator size="4" />

      {/* Application counts */}
      <Flex direction="column" gap="2">
        <Text size="2" weight="medium">
          Applications
        </Text>
        <Grid columns="2" gap="2">
          <CountCard
            icon={<ClipboardList size={14} />}
            label="Total"
            count={event.total_applications}
            href={event.manage_path}
          />
          <CountCard
            icon={<Clock size={14} />}
            label="Pending"
            count={event.pending_count}
            href={event.manage_path}
          />
          <CountCard
            icon={<CheckCircle size={14} />}
            label="Approved"
            count={event.approved_count}
            color="var(--green-11)"
            href={event.manage_path}
          />
          <CountCard
            icon={<XCircle size={14} />}
            label="Rejected"
            count={event.rejected_count}
            color="var(--crimson-11)"
            href={event.manage_path}
          />
          {/* Only events with media auto-approval on can have a waitlist, so a
              permanent zero would be noise on every other event. */}
          {event.waitlisted_count > 0 && (
            <CountCard
              icon={<Hourglass size={14} />}
              label="Waitlisted"
              count={event.waitlisted_count}
              color="var(--amber-11)"
              href={event.manage_path}
            />
          )}
        </Grid>
      </Flex>

      {/* Accepts */}
      {accepts.length > 0 && (
        <>
          <Separator size="4" />
          <Flex direction="column" gap="2">
            <Text size="2" weight="medium">
              Accepts
            </Text>
            <Flex gap="2" wrap="wrap">
              {accepts.map(({ label, icon }) => (
                <Badge key={label} variant="soft" size="2">
                  <Flex align="center" gap="1">
                    {icon}
                    {label}
                  </Flex>
                </Badge>
              ))}
            </Flex>
          </Flex>
        </>
      )}

      <Separator size="4" />
      {/* The way into the event's own page. Above the grid because it is where
          the panel's counts are actually acted on. Present only where the
          section serves that page, like the gallery link below. */}
      {event.manage_path && (
        <Button asChild size="2">
          <InertiaLink href={event.manage_path}>
            <ClipboardList size={14} />
            Manage Applications
          </InertiaLink>
        </Button>
      )}
      <Grid columns="2" gap="2">
        {event.is_published ? (
          <Button
            variant="soft"
            color="crimson"
            size="2"
            onClick={() => router.patch(event.unpublish_path, {}, { preserveState: true, preserveScroll: true })}
          >
            Unpublish
          </Button>
        ) : (
          <Button
            variant="soft"
            color="green"
            size="2"
            onClick={() => router.patch(event.publish_path, {}, { preserveState: true, preserveScroll: true})}
          >
            Publish
          </Button>
        )}
        <Button asChild color="gray" variant="soft" size="2">
          <InertiaLink href={event.edit_path}>
            Edit Event
          </InertiaLink>
        </Button>
        <Button asChild variant="soft" size="2">
          <InertiaLink href={event.participant_check_in_path}>
            Participant Check In
          </InertiaLink>
        </Button>
        <Button
          variant="soft"
          color="gray"
          size="2"
          onClick={() => router.post(event.duplicate_path)}
        >
          Duplicate
        </Button>
        {/* Present only where the section serves galleries — the panel shows
            what it is handed rather than checking which section it is in. */}
        {event.gallery_path && (
          <Button asChild variant="soft" size="2">
            <InertiaLink href={event.gallery_path}>
              <Images size={14} />
              Gallery
            </InertiaLink>
          </Button>
        )}
      </Grid>

      {/* Kept out of the grid above and behind its own separator: the other four
          are reversible, this one is not. */}
      <Separator size="4" />
      <AlertDialog.Root>
        <AlertDialog.Trigger>
          <Button variant="soft" color="red" size="2">
            <Trash2 size={14} />
            Delete Event
          </Button>
        </AlertDialog.Trigger>
        <AlertDialog.Content maxWidth="450px">
          <AlertDialog.Title>Delete {event.name}?</AlertDialog.Title>
          <AlertDialog.Description size="2">
            This cannot be undone. Its {event.total_applications}{" "}
            {event.total_applications === 1 ? "application" : "applications"} and
            any gallery will be deleted along with it.
          </AlertDialog.Description>

          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">
                Keep
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button
                variant="solid"
                color="red"
                onClick={() => router.delete(event.destroy_path)}
              >
                Yes, delete it
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </Flex>
  );
}
